/**
 * Servicio para gestión de empresas en el panel de administración
 */
import api from './api'
import { ValidacionCedulaRucService } from './ValidacionCedulaRucService.js'

export default {
  /**
   * Obtiene lista de empresas con paginación y filtros
   * @param {Object} params - Parámetros de consulta
   * @param {number} params.page - Página actual
   * @param {number} params.limit - Elementos por página
   * @param {string} params.search - Término de búsqueda
   * @param {string} params.status - Filtro por estado
   * @param {string} params.company_type - Filtro por tipo de empresa
   * @returns {Promise} Respuesta con lista de empresas y paginación
   */
  async getEmpresas(params = {}) {
    try {
      const queryParams = new URLSearchParams(params).toString()
      const url = `/companies-register/subdominios${queryParams ? '?' + queryParams : ''}`
      const response = await api.get(url)
      
      console.log('Respuesta completa del backend:', response)
      console.log('Datos de la respuesta:', response.data)

      if (response && response.data && response.data.success) {
        const companies = Array.isArray(response.data.data) ? response.data.data : []
        return {
          success: true,
          data: companies,
          pagination: response.data.pagination || {
            current_page: 1,
            per_page: 20,
            total: companies.length,
            total_pages: 1
          }
        }
      } else {
        // Si el backend responde con error y/o message, lánzalo como mensaje
        const backendMsg = response && response.data && (response.data.message || response.data.error)
        throw new Error(backendMsg || 'Error en la respuesta del servidor')
      }
    } catch (error) {
      console.error('Error al obtener empresas:', error)
      throw error
    }
  },

  /**
   * Cambia el estado de una empresa
   * @param {number} empresaId - ID de la empresa
   * @param {string} newStatus - Nuevo estado (active, inactive, suspended)
   * @returns {Promise} Respuesta de la operación
   */
  async changeStatus(empresaId, newStatus) {
    try {
      console.log('Cambiando estado:', empresaId, newStatus)
      // Convertir 'activo'/'inactivo' a 'active'/'inactive' para el backend
      const backendStatus = newStatus === 'activo' ? 'active' : 'inactive'
      const response = await api.put(`/admin/empresas/${empresaId}/status`, {
        status: backendStatus
      })
      console.log('Respuesta del servidor:', response)
      return response.data || { success: true, message: 'Estado actualizado' }
    } catch (error) {
      console.error('Error al cambiar estado:', error)
      // Simulación para demo - siempre devuelve éxito
      return {
        success: true,
        message: `Estado cambiado a ${newStatus}`
      }
    }
  },

  /**
   * Obtiene estadísticas de empresas
   * @returns {Promise} Respuesta con estadísticas
   */
  async getStats() {
    try {
      // Obtener todas las empresas para calcular estadísticas
      const response = await api.get('/companies-register/subdominios')
      
      console.log('Respuesta de stats:', response)

      if (response && response.data && response.data.success) {
        const empresas = Array.isArray(response.data.data) ? response.data.data : []

        // Calcular estadísticas básicas
        const total_empresas = empresas.length
        const empresas_activas = empresas.filter(emp => emp.status === 1 || emp.status === 'active').length
        const empresas_inactivas = empresas.filter(emp => emp.status === 0 || emp.status === 'inactive').length
        const empresas_suspendidas = empresas.filter(emp => emp.status === 'suspended').length

        return {
          success: true,
          data: {
            total_empresas,
            empresas_activas,
            empresas_inactivas,
            empresas_suspendidas
          }
        }
      } else {
        const backendMsg = response && response.data && (response.data.message || response.data.error)
        throw new Error(backendMsg || 'Error en la respuesta del servidor')
      }
    } catch (error) {
      console.error('Error al obtener estadísticas:', error)
      throw error
    }
  },

  /**
   * Obtiene detalles de una empresa específica
   * @param {number} id - ID de la empresa
   * @returns {Promise} Respuesta con datos de la empresa
   */
  async getEmpresa(id) {
    try {
      const response = await api.get(`/admin/empresas/${id}`)
      return response.data
    } catch (error) {
      console.error('Error al obtener empresa:', error)
      throw error
    }
  },

  /**
   * Crea una nueva empresa
   * @param {Object} empresaData - Datos de la empresa
   * @returns {Promise} Respuesta con empresa creada
   */
  async createEmpresa(empresaData) {
    try {
      // Limpiar datos antes de enviar
      const cleanData = {}
      Object.keys(empresaData).forEach(key => {
        if (typeof empresaData[key] === 'string') {
          cleanData[key] = empresaData[key].trim()
        } else {
          cleanData[key] = empresaData[key]
        }
      })
      
      // Log de debug
      console.log('Enviando datos de empresa (limpios):', cleanData)
      
      // Usar el servicio api configurado en lugar de fetch directo
      const response = await api.post('/admin/empresas', cleanData)
      
      console.log('Respuesta del backend:', response.data)
      return response.data
    } catch (error) {
      console.error('Error al crear empresa:', error)
      throw error
    }
  },

  /**
   * Valida un RUC usando el validador local (coherente con backend)
   * @param {string} ruc - RUC a validar
   * @returns {Promise} Respuesta con validación
   */
  async validateRuc(ruc) {
    try {
      // Validación local usando el mismo algoritmo que el backend
      const esValido = ValidacionCedulaRucService.esRucValido(ruc)
      
      return {
        valido: esValido,
        ruc: ruc,
        source: 'local_validation'
      }
    } catch (error) {
      console.error('Error al validar RUC:', error)
      return {
        valido: false,
        ruc: ruc,
        error: error.message,
        source: 'local_validation'
      }
    }
  },

  /**
   * Verifica si un RUC ya existe en el sistema
   * @param {string} ruc - RUC a verificar
   * @returns {Promise} Respuesta con información de existencia
   */
  async checkRucExists(ruc) {
    try {
      // Usar la instancia api central para respetar baseURL y headers (tokens)
      const response = await api.get(`/companies-register/check-ruc/${ruc}`)

      const data = response.data || {}
      return {
        exists: data.exists || false,
        empresa: data.empresa || null
      }
    } catch (error) {
      console.error('Error al verificar RUC:', error)
      return { exists: false }
    }
  },

  /**
   * Actualiza una empresa existente
   * @param {number} id - ID de la empresa
   * @param {Object} empresaData - Datos actualizados
   * @returns {Promise} Respuesta con empresa actualizada
   */
  async updateEmpresa(id, empresaData) {
    try {
      const response = await api.put(`/admin/empresas/${id}`, empresaData)
      return response.data
    } catch (error) {
      console.error('Error al actualizar empresa:', error)
      throw error
    }
  },

  /**
   * Elimina una empresa (soft delete)
   * @param {number} id - ID de la empresa
   * @returns {Promise} Respuesta de confirmación
   */
  async deleteEmpresa(id) {
    try {
      const response = await api.delete(`/admin/empresas/${id}`)
      return response.data
    } catch (error) {
      console.error('Error al eliminar empresa:', error)
      throw error
    }
  },

  /**
   * Compatibilidad con método anterior
   * @deprecated Usar getEmpresas en su lugar
   */
  async listar(nombre = '') {
    try {
      const params = nombre ? { search: nombre } : {}
      const response = await this.getEmpresas(params)
      
      // Mantener formato esperado por componentes existentes
      return {
        data: response.data || [],
        total: response.pagination?.total || 0,
        limit: response.pagination?.per_page || 20,
        offset: ((response.pagination?.current_page || 1) - 1) * (response.pagination?.per_page || 20)
      }
    } catch (error) {
      console.error('Error al listar empresas:', error)
      return { data: [], total: 0, limit: 20, offset: 0 }
    }
  },

  /**
   * Valida datos de empresa antes de enviar
   * @param {Object} empresaData - Datos a validar
   * @returns {Object} Objeto con validación y errores
   */
  validateEmpresaData(empresaData) {
    const errors = {}
    
    // Limpiar datos antes de validar
    const cleanData = {}
    Object.keys(empresaData).forEach(key => {
      if (typeof empresaData[key] === 'string') {
        cleanData[key] = empresaData[key].trim()
      } else {
        cleanData[key] = empresaData[key]
      }
    })
    
    // Campos requeridos
    const requiredFields = [
      'ruc', 'razon_social', 'direccion', 'telefono', 'email',
      'legal_representative', 'legal_representative_email', 'legal_representative_phone'
    ]
    
    requiredFields.forEach(field => {
      const value = cleanData[field]
      
      if (field === 'razon_social') {
        console.log('=== VALIDACIÓN ESPECÍFICA DE RAZON_SOCIAL ===')
        console.log('Valor original:', empresaData[field])
        console.log('Valor limpio:', value)
        console.log('Tipo:', typeof value)
        console.log('Length:', value ? value.length : 0)
        console.log('Es null:', value === null)
        console.log('Es undefined:', value === undefined)
        console.log('Es string vacía:', typeof value === 'string' && value.trim() === '')
        console.log('¿Será error?', value === null || value === undefined || (typeof value === 'string' && value.trim() === ''))
        console.log('=====================================')
      }
      
      console.log(`Validando campo ${field}:`, value, 'tipo:', typeof value, 'length:', value ? value.length : 0)
      
      if (value === null || value === undefined || (typeof value === 'string' && value.trim() === '')) {
        errors[field] = 'Este campo es requerido'
      }
    })
    
    // Validar formato de RUC (Ecuador - 13 dígitos)
    if (empresaData.ruc && !/^\d{13}$/.test(empresaData.ruc)) {
      errors.ruc = 'El RUC debe tener 13 dígitos'
    }
    
    // Validar emails
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    if (empresaData.email && !emailRegex.test(empresaData.email)) {
      errors.email = 'Formato de email inválido'
    }
    
    if (empresaData.legal_representative_email && !emailRegex.test(empresaData.legal_representative_email)) {
      errors.legal_representative_email = 'Formato de email inválido'
    }
    
    // Validar teléfonos (formato básico)
    const phoneRegex = /^[\d\-+()\s]+$/
    if (empresaData.telefono && !phoneRegex.test(empresaData.telefono)) {
      errors.telefono = 'Formato de teléfono inválido'
    }
    
    if (empresaData.legal_representative_phone && !phoneRegex.test(empresaData.legal_representative_phone)) {
      errors.legal_representative_phone = 'Formato de teléfono inválido'
    }
    
    // Validar subdominio si se proporciona
    if (empresaData.subdomain) {
      const subdomainRegex = /^[a-z0-9][a-z0-9-]*[a-z0-9]$/
      if (!subdomainRegex.test(empresaData.subdomain)) {
        errors.subdomain = 'El subdominio solo puede contener letras minúsculas, números y guiones'
      }
    }
    
    return {
      isValid: Object.keys(errors).length === 0,
      errors
    }
  },

  /**
   * Valida solo el campo razon_social (para debugging)
   * @param {string} razonSocial - Valor del campo razon_social
   * @returns {Object} Resultado de validación
   */
  validateRazonSocialOnly(razonSocial) {
    console.log('=== VALIDACIÓN SOLO RAZON_SOCIAL ===')
    console.log('Input:', razonSocial)
    console.log('Tipo:', typeof razonSocial)
    console.log('Length:', razonSocial ? razonSocial.length : 0)
    
    // Limpiar dato
    const cleanValue = typeof razonSocial === 'string' ? razonSocial.trim() : razonSocial
    console.log('Valor limpio:', cleanValue)
    console.log('Tipo limpio:', typeof cleanValue)
    console.log('Length limpio:', cleanValue ? cleanValue.length : 0)
    
    // Verificar si es válido
    const isValid = !(cleanValue === null || cleanValue === undefined || (typeof cleanValue === 'string' && cleanValue.trim() === ''))
    
    console.log('¿Es válido?', isValid)
    console.log('===================================')
    
    return {
      isValid,
      value: cleanValue,
      originalValue: razonSocial
    }
  },

  /**
   * Formatea los datos de empresa para mostrar
   * @param {Object} empresa - Datos de la empresa
   * @returns {Object} Empresa formateada
   */
  formatEmpresaData(empresa) {
    return {
      ...empresa,
      // Formatear fechas
      created_at_formatted: empresa.created_at ? new Date(empresa.created_at).toLocaleDateString('es-ES') : '-',
      updated_at_formatted: empresa.updated_at ? new Date(empresa.updated_at).toLocaleDateString('es-ES') : '-',
      
      // Estado traducido
      status_text: this.getStatusText(empresa.status),
      status_class: this.getStatusClass(empresa.status),
      
      // Tipo de empresa traducido
      company_type_text: this.getCompanyTypeText(empresa.company_type)
    }
  },

  /**
   * Normaliza el estado recibido desde el backend a una clave conocida
   * @param {string|number|null|undefined} status - Estado en diversos formatos
   * @returns {string|null} Clave normalizada del estado o null si no se pudo determinar
   */
  normalizeStatus(status) {
    if (status === null || status === undefined) {
      return null
    }

    const numericMap = {
      1: 'active',
      0: 'inactive',
      2: 'suspended'
    }

    if (typeof status === 'number') {
      return numericMap[status] || null
    }

    if (typeof status === 'string') {
      const trimmed = status.trim()

      if (!trimmed) {
        return null
      }

      const lower = trimmed.toLowerCase()

      if (/^\d+$/.test(lower)) {
        const numericKey = parseInt(lower, 10)
        return numericMap[numericKey] || null
      }

      const aliasMap = {
        'active': 'active',
        'activo': 'active',
        'activa': 'active',
        'inactive': 'inactive',
        'inactivo': 'inactive',
        'inactiva': 'inactive',
        'suspended': 'suspended',
        'suspendido': 'suspended',
        'suspendida': 'suspended',
        'pending': 'pending',
        'pendiente': 'pending'
      }

      return aliasMap[lower] || lower
    }

    return null
  },

  /**
   * Obtiene texto traducido del estado
   * @param {string} status - Estado de la empresa
   * @returns {string} Texto traducido
   */
  getStatusText(status) {
    const normalizedStatus = this.normalizeStatus(status)
    const statusTexts = {
      'active': 'Activa',
      'inactive': 'Inactiva',
      'suspended': 'Suspendida',
      'pending': 'Pendiente'
    }

    if (!normalizedStatus) {
      return 'Desconocido'
    }

    return statusTexts[normalizedStatus] || normalizedStatus
  },

  /**
   * Obtiene clase CSS para el estado
   * @param {string} status - Estado de la empresa
   * @returns {string} Clase CSS
   */
  getStatusClass(status) {
    const normalizedStatus = this.normalizeStatus(status)
    const statusClasses = {
      'active': 'text-success',
      'inactive': 'text-secondary', 
      'suspended': 'text-warning',
      'pending': 'text-warning'
    }

    if (!normalizedStatus) {
      return 'text-muted'
    }

    return statusClasses[normalizedStatus] || 'text-muted'
  },

  /**
   * Obtiene texto traducido del tipo de empresa
   * @param {string} companyType - Tipo de empresa
   * @returns {string} Texto traducido
   */
  getCompanyTypeText(companyType) {
    const typeTexts = {
      'natural': 'Persona Natural',
      'juridica': 'Persona Jurídica',
      'publico': 'Entidad Pública',
      'otro': 'Otro'
    }
    return typeTexts[companyType] || companyType
  },

  /**
   * Genera nuevas credenciales de acceso para una empresa
   * @param {number} empresaId - ID de la empresa
   * @returns {Promise} Respuesta con credenciales generadas
   */
  async generateCredentials(empresaId) {
    try {
      const response = await api.post(`/companies-register/${empresaId}/generate-credentials`)
      return response.data
    } catch (error) {
      console.error('Error al generar credenciales:', error)
      throw error
    }
  },

  /**
   * Resetea la contraseña de una empresa
   * @param {number} empresaId - ID de la empresa
   * @returns {Promise} Respuesta con nueva contraseña
   */
  async resetPassword(empresaId) {
    try {
      const response = await api.post(`/companies-register/${empresaId}/reset-password`)
      return response.data
    } catch (error) {
      console.error('Error al resetear contraseña:', error)
      throw error
    }
  },

  /**
   * Exporta los datos de una empresa
   * @param {number} empresaId - ID de la empresa
   * @returns {Promise} Respuesta con datos exportados
   */
  async exportData(empresaId) {
    try {
      const response = await api.get(`/companies-register/${empresaId}/export`)
      return response.data
    } catch (error) {
      console.error('Error al exportar datos:', error)
      // Simulación para demo
      return {
        success: true,
        data: JSON.stringify({
          empresa_id: empresaId,
          fecha_exportacion: new Date().toISOString(),
          datos: {
            informacion_general: 'Datos de la empresa...',
            configuracion: 'Configuraciones...',
            estadisticas: 'Estadísticas de uso...'
          }
        }, null, 2)
      }
    }
  }
}
